import classNames from 'classnames'
import { type ReactNode } from 'react'
import { observer } from 'mobx-react-lite'
import styles from 'shared/ui/CardsFilter/styles.module.scss'
import { type ITooltipProps, Tooltip } from 'shared/ui/Tooltip'
import { Typography } from 'shared/ui/Typography'
import { numberFormat } from 'shared/lib'

export type ICardFilter<T = string> = {
  key: T
  title: string
  amount?: number | null
  amountContent?: ReactNode
  percentage?: number | null
  active?: boolean
  disabled?: boolean
  notInteractable?: boolean
  tooltipProps?: ITooltipProps
}

export type ICardFilterProps<T> = {
  activeKey?: T
  type?: 'detailed' | 'simple'
  onActiveFilter?: (tab: ICardFilter<T>) => void
}

type ICardFilterPropsExpanded<T> = ICardFilterProps<T> & {
  item: ICardFilter<T>
}

export const CardFilter = observer(
  <T,>({ item, onActiveFilter, activeKey, type = 'detailed' }: ICardFilterPropsExpanded<T>) => {
    const {
      key,
      title,
      amount,
      amountContent,
      percentage,
      active,
      disabled,
      notInteractable,
      tooltipProps,
    } = item

    const handleTabClick = () => {
      onActiveFilter && onActiveFilter(item)
    }

    const amountContentRender = () => {
      if (amountContent) {
        return amountContent
      }
      if (amount === undefined || amount === null) {
        return '—'
      }
      return numberFormat({ value: amount || 0 })
    }

    const card = (
      <div
        key={key as string}
        className={classNames(styles.item, {
          [styles.active]: active || activeKey === key,
          [styles.disabled]: disabled,
          [styles.notInteractable]: notInteractable,
          [styles.detailedType]: type === 'detailed',
          [styles.simpleType]: type === 'simple',
        })}
        onClick={handleTabClick}
      >
        <Typography ariaLabel={'tab-title'} variant={'body-rg-regular'} className={styles.title}>
          {title}
        </Typography>
        <Typography ariaLabel={'tab-amount'} variant={'heading-md'} className={styles.amount}>
          {amountContentRender()}
        </Typography>
        {type === 'detailed' && (
          <Typography
            ariaLabel={'tab-percentage'}
            variant={'body-sm-regular'}
            className={styles.percentage}
          >
            {percentage === undefined || percentage === null ? '—' : `${percentage}%`}
          </Typography>
        )}
      </div>
    )

    if (tooltipProps) {
      return <Tooltip {...tooltipProps}>{card}</Tooltip>
    }

    return card
  }
)
